import { supabase } from '@/integrations/supabase/client';
import type { Tables, Json } from '@sylva/shared-types/database';
import type { EnhancedBusinessHours } from '@/lib/businessHours';
import { defaultBusinessHours } from '@/lib/businessHours';

export type TeamMemberRow = Tables<'team_members'>;

export interface TeamMember {
  id: string;
  tenant_id: string;
  name: string;
  email?: string;
  phone?: string;
  role: string;
  color?: string;
  services?: string[]; // Populated from team_member_services join
  availability: EnhancedBusinessHours;
  paused_at?: string | null;
  paused_until?: string | null;
  pause_reason?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Is the team member currently paused (compute-on-read)?
 * paused_until in the past means the pause has expired and the member is active again.
 */
export function isPaused(
  m: Pick<TeamMember, 'paused_at' | 'paused_until'>,
): boolean {
  if (!m.paused_at) return false;
  if (!m.paused_until) return true; // unbefristet
  return new Date(m.paused_until) > new Date(); // befristet & noch laufend
}

export const fetchTeamMembers = async (tenantId: string, includeDeleted = false): Promise<TeamMember[]> => {
  let query = supabase
    .from('team_members')
    .select(`
      *,
      team_member_services (appointment_type_id)
    `)
    .eq('tenant_id', tenantId);

  // By default, exclude soft-deleted team members
  if (!includeDeleted) {
    query = query.is('deleted_at', null);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) throw error;

  const transformedMembers: TeamMember[] = (data || []).map(member => ({
    ...member,
    email: member.email ?? undefined,
    phone: member.phone ?? undefined,
    role: member.role ?? '',
    color: member.color ?? undefined,
    availability: (member.availability as unknown as TeamMember['availability']) || defaultBusinessHours,
    services: (member.team_member_services as any[] || []).map((service: any) => service.appointment_type_id)
  }));

  return transformedMembers;
};

export const createTeamMember = async (
  tenantId: string,
  teamMember: Omit<TeamMember, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>
): Promise<TeamMember> => {
  const { data, error } = await supabase
    .from('team_members')
    .insert([{
      tenant_id: tenantId,
      name: teamMember.name,
      email: teamMember.email,
      phone: teamMember.phone,
      role: teamMember.role,
      color: teamMember.color,
      availability: teamMember.availability as unknown as Json
      // Note: services are handled separately via team_member_services
    }])
    .select()
    .single();

  if (error) throw error;

  const transformedMember: TeamMember = {
    ...data,
    email: data.email ?? undefined,
    phone: data.phone ?? undefined,
    role: data.role ?? '',
    color: data.color ?? undefined,
    availability: (data.availability as unknown as TeamMember['availability']) || defaultBusinessHours,
    services: [] // Will be populated separately
  };

  return transformedMember;
};

export const updateTeamMember = async (
  id: string,
  updates: Partial<Omit<TeamMember, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>>
): Promise<TeamMember> => {
  const updateData: Record<string, string | Json | undefined> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.email !== undefined) updateData.email = updates.email;
  if (updates.phone !== undefined) updateData.phone = updates.phone;
  if (updates.role !== undefined) updateData.role = updates.role;
  if (updates.color !== undefined) updateData.color = updates.color;
  if (updates.availability !== undefined) updateData.availability = updates.availability as unknown as Json;
  // Note: services are handled separately via team_member_services

  const { data, error } = await supabase
    .from('team_members')
    .update(updateData)
    .eq('id', id)
    .select(`
      *,
      team_member_services (appointment_type_id)
    `)
    .single();

  if (error) throw error;

  const transformedMember: TeamMember = {
    ...data,
    email: data.email ?? undefined,
    phone: data.phone ?? undefined,
    role: data.role ?? '',
    color: data.color ?? undefined,
    availability: (data.availability as unknown as TeamMember['availability']) || defaultBusinessHours,
    services: (data.team_member_services as any[] || []).map((service: any) => service.appointment_type_id)
  };

  return transformedMember;
};

/**
 * Pause a team member: hides them from all customer booking channels while keeping
 * them visible internally. Also disables their staff login (if any).
 * @param until  optional ISO timestamp — pause expires then; omit/null = unbefristet.
 * @param reason optional free-text reason (Urlaub / Elternzeit / Krankheit / …).
 */
export const pauseTeamMember = async (
  id: string,
  until?: string | null,
  reason?: string | null,
): Promise<void> => {
  // login_active added by migration 20260626120000; not in generated types.
  const { error } = await (supabase.from('team_members') as any)
    .update({
      paused_at: new Date().toISOString(),
      paused_until: until ?? null,
      pause_reason: reason ?? null,
      login_active: false, // pause also disables the staff login
    })
    .eq('id', id);

  if (error) throw error;
};

/**
 * End a team member's pause: they become bookable again. Re-enables the staff login
 * only if a login_username is set (otherwise there is no login to activate).
 */
export const resumeTeamMember = async (id: string): Promise<void> => {
  // login_* added by migration 20260626120000; not in generated types.
  const { data: row, error: readError } = await (supabase.from('team_members') as any)
    .select('login_username')
    .eq('id', id)
    .single();

  if (readError) throw readError;

  const hasLogin = !!row?.login_username;

  const { error } = await (supabase.from('team_members') as any)
    .update({
      paused_at: null,
      paused_until: null,
      pause_reason: null,
      login_active: hasLogin, // only re-activate a login that actually exists
    })
    .eq('id', id);

  if (error) throw error;
};

export interface FutureAppointmentsCheck {
  hasFutureAppointments: boolean;
  count: number;
}

export const checkTeamMemberFutureAppointments = async (teamMemberId: string): Promise<FutureAppointmentsCheck> => {
  const now = new Date().toISOString();

  const { count, error } = await supabase
    .from('appointments')
    .select('*', { count: 'exact', head: true })
    .eq('team_member_id', teamMemberId)
    .neq('status', 'cancelled') // Stornierte Termine sind keine Verpflichtung und dürfen das Löschen nicht blockieren
    .gt('start_time', now);

  if (error) throw error;

  return {
    hasFutureAppointments: (count || 0) > 0,
    count: count || 0
  };
};

export class TeamMemberHasFutureAppointmentsError extends Error {
  constructor(public count: number) {
    super(`Cannot delete team member. They have ${count} future appointment(s).`);
    this.name = 'TeamMemberHasFutureAppointmentsError';
  }
}

export const deleteTeamMember = async (id: string): Promise<void> => {
  // Check for future appointments first
  const { hasFutureAppointments, count } = await checkTeamMemberFutureAppointments(id);

  if (hasFutureAppointments) {
    throw new TeamMemberHasFutureAppointmentsError(count);
  }

  // Soft delete: set deleted_at timestamp instead of hard delete
  const { error } = await supabase
    .from('team_members')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
};
